/**
 * Coerces the CMS layout into the shape the renderer dereferences.
 *
 * The layout is tenant-authored data with no schema on the backend, and the
 * legacy components read it unguarded (`footers.options.id`,
 * `headers.map(...)`, `block.options.style`). One missing or null field there
 * used to take the whole site down with Next's "Application error" screen,
 * e.g. `footers: null` on a tenant that has not configured a footer yet.
 *
 * Rules: lists are always arrays of objects, entries that are not objects are
 * dropped, a footer is an object or null, and every object a component reads
 * `.options` from has an `options` object. Fields this file does not mention
 * pass through untouched.
 */

type AnyRecord = Record<string, any>;

const isRecord = (value: unknown): value is AnyRecord =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const records = (value: unknown): AnyRecord[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const withOptions = <T extends AnyRecord>(value: T): T & { options: AnyRecord } => ({
  ...value,
  options: isRecord(value.options) ? value.options : {},
});

// A block without a component name renders nothing anyway (feature.js's
// default case), so it is dropped here rather than carried as a hole.
const normalizeBlocks = (body: unknown) =>
  records(body)
    .filter((block) => typeof block.component === "string")
    .map(withOptions);

export function normalizeLayout<T extends AnyRecord>(layout: T) {
  const config: AnyRecord = isRecord(layout.config) ? { ...layout.config } : {};
  // Read as `config.fabButton.display` / `.options` whenever it is not undefined.
  if (config.fabButton !== undefined) {
    if (isRecord(config.fabButton)) config.fabButton = withOptions(config.fabButton);
    else delete config.fabButton;
  }

  return {
    ...layout,
    config,
    headers: records(layout.headers),
    footers: isRecord(layout.footers) ? withOptions(layout.footers) : null,
    pages: records(layout.pages)
      .filter((page) => typeof page.href === "string")
      .map((page) => ({ ...page, body: normalizeBlocks(page.body) })),
  };
}
